import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header, Footer } from "@/components/SiteChrome";
import { ProductTile } from "@/components/ProductTile";
import { vehicle } from "@/lib/vehicle";
import api from "@/lib/api";

export default function CollectionBrand() {
    const { brand } = useParams();
    const [data, setData] = useState(null);
    const [cat, setCat] = useState("All");
    const [allBrands, setAllBrands] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setNotFound(false);
        api.get(`/brands/${brand}`).then(({ data }) => {
            setData(data);
            // Persist the selected vehicle so it follows the user through PDP + Cart
            vehicle.set({ slug: data.brand.slug, name: data.brand.name, tagline: data.brand.tagline });
        }).catch(() => setNotFound(true));
        api.get("/brands").then(({ data }) => setAllBrands(data.brands));
    }, [brand]);

    const filtered = useMemo(() => {
        if (!data) return [];
        if (cat === "All") return data.products;
        return data.products.filter((p) => p.category === cat);
    }, [data, cat]);

    const presentCats = useMemo(() => {
        if (!data) return [];
        return Array.from(new Set(data.products.map((p) => p.category)));
    }, [data]);

    if (notFound) {
        return (
            <div className="min-h-screen bg-[hsl(var(--dust-50))]">
                <Header />
                <div className="mx-auto max-w-3xl px-4 py-32 text-center">
                    <div className="font-display text-6xl uppercase italic text-[hsl(var(--ink))]">Collection not found.</div>
                    <Link to="/collections" className="mt-6 inline-block font-heading text-sm uppercase underline underline-offset-8">
                        See all collections
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[hsl(var(--dust-50))]">
                <Header />
                <div className="mx-auto max-w-7xl px-4 py-32" />
                <Footer />
            </div>
        );
    }

    const b = data.brand;

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="collection-brand-page">
            <Header />

            <div className="border-b-2 border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <nav className="font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--dust-100))]">
                        <Link to="/" className="hover:text-[hsl(var(--dust-50))]">Home</Link> /{" "}
                        <Link to="/collections" className="hover:text-[hsl(var(--dust-50))]">Collections</Link> /{" "}
                        <span className="text-[hsl(var(--primary))]">{b.name}</span>
                    </nav>
                    <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                        <div>
                            <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--primary))]">
                                Vehicle collection
                            </div>
                            <h1 className="mt-2 font-display text-6xl uppercase italic md:text-8xl">{b.name}</h1>
                            <p className="mt-3 font-heading text-base uppercase tracking-wider text-[hsl(var(--rust))]">
                                {b.tagline}
                            </p>
                            <p className="mt-4 max-w-2xl text-[hsl(var(--dust-100))]">{b.blurb}</p>
                        </div>
                        <div className="font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--dust-100))]">
                            <div>Models · {b.models.slice(0, 4).join(" · ")}</div>
                            <div className="mt-1">{data.count} parts ready</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                <div className="flex flex-wrap items-center gap-2">
                    {["All", ...presentCats].map((c) => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            className={`border-2 px-4 py-2 font-heading text-xs uppercase tracking-wider transition-colors ${
                                cat === c
                                    ? "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]"
                                    : "border-[hsl(var(--dust-300))] text-[hsl(var(--ink-soft))] hover:border-[hsl(var(--ink))] hover:text-[hsl(var(--ink))]"
                            }`}
                            data-testid={`brand-cat-${c.toLowerCase().replace(/\s|&/g, "")}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                <div className="mb-6 mt-6 flex items-center justify-between font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    <span>{filtered.length} parts</span>
                    <Link to="/shop" className="underline underline-offset-8 hover:text-[hsl(var(--ink))]">
                        Browse all gear →
                    </Link>
                </div>

                {filtered.length === 0 ? (
                    <div className="border-2 border-dashed border-[hsl(var(--dust-300))] p-12 text-center">
                        <div className="font-display text-3xl uppercase italic">No parts in this category yet.</div>
                        <p className="mt-2 text-[hsl(var(--ink-soft))]">More {b.name}-fit gear is on the way.</p>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {filtered.map((p) => <ProductTile key={p.slug} p={p} />)}
                    </div>
                )}
            </div>

            <section className="border-t-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] text-[hsl(var(--dust-50))]">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--dust-100))]">
                        Other vehicles
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                        {allBrands.filter((x) => x.slug !== b.slug).map((x) => (
                            <Link
                                key={x.slug}
                                to={`/collections/${x.slug}`}
                                className="border-2 border-[hsl(var(--ink))] bg-card p-4 text-[hsl(var(--ink))] transition-transform hover:-translate-y-0.5"
                            >
                                <div className="font-display text-3xl uppercase italic">{x.name}</div>
                                <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                                    {x.count} parts →
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
